"use client";

import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  managedValidatorStakeAction,
  managedValidatorUnstakeAction,
  patchManagedValidatorUnstakeAction,
  updateApplicationDataAfterStakeAction,
  unstakeApplicationAction,
  withdrawUnstakeApplicationAction,
} from "@/resources/actions/user.actions";
import { createManagedNodeAction } from "@/resources/actions/admin.actions";
import { destroyManagedNodeAction } from "@/resources/actions/admin.actions";
import { queryKeys } from "@/lib/query-keys";
import type {
  ManagedValidatorStakeRequest,
  ManagedValidatorUnstakeRequest,
  UpdateApplicationDataAfterStakeRequest,
  UnstakeApplicationRequest,
  WithdrawUnstakeApplicationRequest,
} from "@/resources/interfaces/user.interface";
import type { CreateManagedNodeRequest } from "@/resources/interfaces/admin.interface";

/**
 * Mutation hook — stake for managed validator.
 * On success, the validator applications cache is invalidated.
 */
export function useManagedValidatorStake() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: ManagedValidatorStakeRequest) => {
      const result = await managedValidatorStakeAction(data);
      if (result.error) throw new Error(result.error);
      return result.data;
    },
    onSuccess: (_data) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.validators.applications.all,
      });
      if (_data?.application.id) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.validators.applications.detail(
            _data.application.id,
          ),
        });
      }
    },
  });
}

/**
 * Mutation hook — unstake managed validator.
 * On success, the validator applications cache is invalidated.
 */
export function useManagedValidatorUnstake() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: ManagedValidatorUnstakeRequest) => {
      const result = await managedValidatorUnstakeAction(data);
      if (result.error) throw new Error(result.error);
      return result.data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.validators.applications.all,
      });
      if (variables.application_id) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.validators.applications.detail(
            variables.application_id,
          ),
        });
      }
      if (variables.wallet) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.userDashboard.activeValidators(variables.wallet),
        });
        queryClient.invalidateQueries({
          queryKey: queryKeys.validators.applications.list(),
        });
      }
    },
  });
}

/**
 * Mutation hook — PATCH managed validator unstake with confirmed tx hash.
 * On success, validator applications cache is invalidated.
 */
export function usePatchManagedValidatorUnstake() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: UnstakeApplicationRequest) => {
      const result = await patchManagedValidatorUnstakeAction(data);
      if (result.error) throw new Error(result.error);
      return result.data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.validators.applications.all,
      });
      if (variables.application_id) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.validators.applications.detail(
            variables.application_id,
          ),
        });
        queryClient.invalidateQueries({
          queryKey: queryKeys.validators.applications.unstakeInfo(
            variables.application_id,
          ),
        });

        queryClient.invalidateQueries({
          queryKey: queryKeys.userDashboard.statistics(variables.wallet),
        });

        queryClient.invalidateQueries({
          queryKey: queryKeys.userDashboard.activeValidators(variables.wallet),
        });
      }
      if (variables.wallet) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.userDashboard.activeValidators(variables.wallet),
        });
        queryClient.invalidateQueries({
          queryKey: queryKeys.validators.applications.list(),
        });
      }
    },
  });
}

/**
 * Mutation hook — update managed validator stake tx hash after confirmation.
 * On success, validator applications cache is invalidated.
 */
export function useUpdateApplicationDataAfterStake() {
  return useMutation({
    mutationFn: async (data: UpdateApplicationDataAfterStakeRequest) => {
      const result = await updateApplicationDataAfterStakeAction(data);
      if (result.error) throw new Error(result.error);
      return result.data;
    },
    onSuccess: (_data, variables) => {
      // queryClient.invalidateQueries({
      //   queryKey: queryKeys.validators.applications.all,
      // });
      // queryClient.invalidateQueries({
      //   queryKey: queryKeys.validators.applications.detail(
      //     variables.application_id,
      //   ),
      // });
      // queryClient.invalidateQueries({
      //   queryKey: queryKeys.validators.applications.applicationDataAfterStake(
      //     variables.application_id,
      //     variables.wallet,
      //   ),
      // });
    },
  });
}

/**
 * Mutation hook — finalize managed validator unstake with tx_hash.
 * On success, validator applications cache is invalidated.
 */
export function useUnstakeApplication(options?: {
  invalidateOnSuccess?: boolean;
}) {
  const queryClient = useQueryClient();
  const invalidateOnSuccess = options?.invalidateOnSuccess ?? true;

  return useMutation({
    mutationFn: async (data: UnstakeApplicationRequest) => {
      const result = await unstakeApplicationAction(data);
      if (result.error) throw new Error(result.error);
      return result.data;
    },
    onSuccess: (_data, variables) => {
      if (!invalidateOnSuccess) return;

      queryClient.invalidateQueries({
        queryKey: queryKeys.validators.applications.all,
      });
      if (variables.application_id) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.validators.applications.detail(
            variables.application_id,
          ),
        });
      }
      if (variables.wallet) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.userDashboard.activeValidators(variables.wallet),
        });
        queryClient.invalidateQueries({
          queryKey: queryKeys.validators.applications.list(),
        });
      }
    },
  });
}

/**
 * Mutation hook — create managed node (admin operation).
 * On success, admin applications cache is invalidated.
 */
export function useCreateManagedNode() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateManagedNodeRequest) => {
      const result = await createManagedNodeAction(data);
      if (result.error) throw new Error(result.error);
      return result.data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.admin.applications.all,
      });
      if (variables.application_id) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.admin.applications.detail(
            variables.application_id,
          ),
        });
        queryClient.invalidateQueries({
          queryKey: queryKeys.validators.applications.listByWallet(
            variables.application_id,
          ),
        });
      }
    },
  });
}

/**
 * Mutation hook — destroy managed node (admin operation).
 * On success, admin applications cache is invalidated.
 */
export function useDestroyManagedNode() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (applicationId: string) => {
      const result = await destroyManagedNodeAction(applicationId);
      if (result.error) throw new Error(result.error);
      return result.data;
    },
    onSuccess: (_data, applicationId) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.admin.applications.all,
      });
      if (applicationId) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.admin.applications.detail(applicationId),
        });
        queryClient.invalidateQueries({
          queryKey: queryKeys.validators.applications.list(),
        });
      }
    },
  });
}

/**
 * Mutation hook — withdraw unstaked managed validator application.
 * On success, validator applications cache is invalidated.
 */
export function useWithdrawUnstakeApplication() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: WithdrawUnstakeApplicationRequest) => {
      const result = await withdrawUnstakeApplicationAction(data);
      if (result.error) throw new Error(result.error);
      return result.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.validators.applications.all,
      });
    },
  });
}
